import { useEffect, useState } from 'react';
import { useOp } from '../api.ts';
import { useRouter } from '../router.tsx';
import type { MerchantList } from '../types.ts';
import { useChanges } from '../ui/changes.ts';
import { step, useKeys } from '../ui/keys.ts';
import { Empty, ErrorBox, Loading } from '../ui/kit.tsx';
import { SearchBox } from '../ui/SearchBox.tsx';
import { AddMerchantButton, MerchantBulkBar, MerchantTabs } from './catalog/routes.tsx';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'shown', label: 'Shown' },
  { value: 'not_shown', label: 'Not shown' },
  { value: 'pending', label: 'With review items' },
] as const;

export function Merchants() {
  const { query, setQuery, navigate } = useRouter();
  const changes = useChanges();
  const q = query.get('q') ?? '';
  const filter = query.get('filter') ?? 'all';
  const list = useOp<MerchantList>('admin.merchants.list', { q: q || undefined, filter, limit: 200 }, changes);
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const rows = list.data?.rows ?? [];
  useEffect(() => setCursor(0), [q, filter]);
  const toggle = (ref: string) => setSelected((s) => (s.includes(ref) ? s.filter((r) => r !== ref) : [...s, ref]));

  useKeys({
    j: () => setCursor((c) => step(c, 1, rows.length)),
    k: () => setCursor((c) => step(c, -1, rows.length)),
    x: () => rows[cursor] && toggle(rows[cursor]!.ref),
    enter: () => rows[cursor] && navigate(`/merchants/${rows[cursor]!.ref}`),
    o: () => rows[cursor] && navigate(`/merchants/${rows[cursor]!.ref}`),
  });

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Merchants</h1>
          <MerchantTabs />
          <p className="muted">
            {list.data ? `${list.data.total} in ${list.data.market} · counts cover ${list.data.sites.map((s) => s.name).join(', ')}` : ' '}
          </p>
        </div>
        {list.data ? <AddMerchantButton market={list.data.market} sites={list.data.sites} /> : null}
      </header>
      <MerchantBulkBar selected={selected} sites={list.data?.sites ?? []} onDone={() => setSelected([])} />
      <div className="toolbar">
        <SearchBox value={q} onChange={(value) => setQuery({ q: value || null }, { replace: true })} placeholder="Search merchants" />
        <div className="segmented" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <button key={f.value} type="button" aria-pressed={filter === f.value} onClick={() => setQuery({ filter: f.value === 'all' ? null : f.value }, { replace: true })}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No merchants match.</Empty> : null}
      {rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col" className="check">
                <span className="visually-hidden">Select</span>
              </th>
              <th scope="col">Merchant</th>
              <th scope="col" className="num">Shown on</th>
              <th scope="col" className="num">Current codes</th>
              <th scope="col" className="num">Current deals</th>
              <th scope="col" className="num">In review</th>
              <th scope="col" className="num">Popularity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.ref}
                className={index === cursor ? 'cursor' : undefined}
                onClick={() => navigate(`/merchants/${row.ref}`)}
                onMouseEnter={() => setCursor(index)}
              >
                <td className="check" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Select ${row.name}`} checked={selected.includes(row.ref)} onChange={() => toggle(row.ref)} />
                </td>
                <td>
                  <a href={`/merchants/${row.ref}`} onClick={(e) => e.preventDefault()} className="strong">
                    {row.name}
                  </a>
                  {row.status !== 'active' ? <span className="badge badge-muted">inactive</span> : null}
                </td>
                <td className="num">
                  {row.shownOn}/{list.data!.sites.length}
                </td>
                <td className={`num ${row.currentCodes === 0 && row.shownOn > 0 ? 'text-warn' : ''}`}>{row.currentCodes}</td>
                <td className="num">{row.currentDeals}</td>
                <td className="num">{row.pending > 0 ? <span className="badge badge-review">{row.pending}</span> : '–'}</td>
                <td className="num muted">{row.popularity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
